/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AddPopUpComponent} from '../../../core/components/pop-ups/add-pop-up/add-pop-up.component';
import {DeletePopUpComponent} from '../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {PropulsionTypeService} from '../../../services/propulsion-type.service';

@Component({
    selector: 'app-manage-propulsion-types',
    templateUrl: './manage-propulsion-types.component.html',
    styleUrls: ['./manage-propulsion-types.component.scss'],
    standalone: false
})
export class ManagePropulsionTypesComponent implements OnInit {
  showSpinner: boolean;
  initialLoad: boolean;
  noData: boolean;

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  columnHeaders: ColDef[] = [
    {field: 'id', headerName: 'Propulsion Type ID'},
    {field: 'propulsionType', headerName: 'Propulsion Type'},
  ];
  displayedColumns: string[];

  propulsionTypes: string[] = [];
  selectedValue: any;

  constructor(public snackBar: FabMessageService, private readonly propulsionTypeService: PropulsionTypeService,
              public dialog: MatDialog) {
  }


  ngOnInit(): void {
    this.loadPropulsionTypes();
  }

  /**
   * fetches data from backend, loads it into propulsionTypes, and displays it in the table
   * if list is empty, hides table and displays message telling the user the table is empty
   * @return void
   */
  loadPropulsionTypes(): void {
    this.showSpinner = true;
    this.initialLoad = false;
    this.propulsionTypeService.getPropulsionTypes().subscribe(
      data => {
        this.propulsionTypes = data;

      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.showSpinner = false;
        this.initialLoad = true;
        this.noData = (this.propulsionTypes.length === 0);
      }
    );
  }


  /**
   * Event for selecting a propulsion type
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedValue = (event.data);
    } else if (event.data.id === this.selectedValue.id) {
      this.selectedValue = null;
    }
  }

  /**
   * Adds a new Propulsion Type to DB.
   */
  addPropulsionType(): void {
    const dialogRef = this.dialog.open(AddPopUpComponent, {
      data: {
        title: 'Add',
        maxLength: 50
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.propulsionTypeService.addPropulsionType(res.data.newValue).subscribe({
          error: err => {
            this.errorHandle(err);
            this.loadPropulsionTypes();
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully added propulsion type.', DISMISS_MSG);
            this.loadPropulsionTypes();
          }
        });
      }
    });
  }

  /**
   * Update selected Propulsion Type with new value.
   */
  updatePropulsionType(): void {
    const dialogRef = this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update',
        currentValueToUpdate: this.selectedValue.propulsionType,
        hidePreview: true,
        maxLength: 50
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.propulsionTypeService.updatePropulsionType(this.selectedValue.id, res.data.newValue).subscribe({
          error: err => {
            this.errorHandle(err);
            this.loadPropulsionTypes();
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully updated propulsion type.', DISMISS_MSG);
            this.loadPropulsionTypes();
            this.selectedValue = undefined;
          }
        });
      }
    });
  }

  /**
   * Deletes a selected Propulsion Type from DB.
   */
  deletePropulsionType(): void {
    const dialogRef = this.dialog.open(DeletePopUpComponent, {
      data: {
        title: 'Delete',
        valueToDelete: this.selectedValue.propulsionType
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res.data.deleteValue) {
        this.propulsionTypeService.deletePropulsionType(this.selectedValue.id).subscribe({
          error: err => {
            this.errorHandle(err);
            this.loadPropulsionTypes();
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully removed propulsion type.', DISMISS_MSG);
            this.loadPropulsionTypes();
            this.selectedValue = undefined;
          }
        });
      }
    });
  }

  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedValue = undefined;
    this.snackBar.displayError(error, DISMISS_MSG);
  }

}
